import { jsPDF } from "jspdf"
import "svg2pdf.js" // Registers doc.svg() method on jsPDF instances
import type { Layer, DrawObject, Point, LineStyle } from "./pcb-types"

// Helper to darken a hex color
function darkenColor(hex: string, factor: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (result) {
    const r = Math.round(parseInt(result[1], 16) * factor)
    const g = Math.round(parseInt(result[2], 16) * factor)
    const b = Math.round(parseInt(result[3], 16) * factor)
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
  }
  return hex
}

// Create probe SVG element matching the canvas drawing exactly
function createProbeSVG(color: string, probeSize: number): SVGSVGElement {
  const scale = probeSize / 153
  const width = 30 * scale
  const height = 153 * scale
  
  const darkColor = darkenColor(color, 0.6)
  
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  svg.setAttribute("xmlns", "http://www.w3.org/2000/svg")
  svg.setAttribute("width", String(width))
  svg.setAttribute("height", String(height))
  svg.setAttribute("viewBox", "-15 0 30 153")
  
  // Define gradients
  const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs")
  
  // Body gradient
  const bodyGrad = document.createElementNS("http://www.w3.org/2000/svg", "linearGradient")
  bodyGrad.setAttribute("id", "bodyGrad")
  bodyGrad.setAttribute("x1", "-8")
  bodyGrad.setAttribute("y1", "0")
  bodyGrad.setAttribute("x2", "8")
  bodyGrad.setAttribute("y2", "0")
  bodyGrad.setAttribute("gradientUnits", "userSpaceOnUse")
  bodyGrad.innerHTML = `
    <stop offset="0%" stop-color="${darkColor}"/>
    <stop offset="50%" stop-color="${color}"/>
    <stop offset="100%" stop-color="${darkColor}"/>
  `
  defs.appendChild(bodyGrad)
  
  // Tip gradient (gold)
  const tipGrad = document.createElementNS("http://www.w3.org/2000/svg", "linearGradient")
  tipGrad.setAttribute("id", "tipGrad")
  tipGrad.setAttribute("x1", "-4")
  tipGrad.setAttribute("y1", "0")
  tipGrad.setAttribute("x2", "4")
  tipGrad.setAttribute("y2", "0")
  tipGrad.setAttribute("gradientUnits", "userSpaceOnUse")
  tipGrad.innerHTML = `
    <stop offset="0%" stop-color="#b8860b"/>
    <stop offset="50%" stop-color="#ffd700"/>
    <stop offset="100%" stop-color="#daa520"/>
  `
  defs.appendChild(tipGrad)
  
  svg.appendChild(defs)
  
  // 1. Gold tip (triangle)
  const tip = document.createElementNS("http://www.w3.org/2000/svg", "polygon")
  tip.setAttribute("points", "0,0 -4,40 4,40")
  tip.setAttribute("fill", "url(#tipGrad)")
  tip.setAttribute("stroke", "#000000")
  tip.setAttribute("stroke-width", "1")
  tip.setAttribute("stroke-linejoin", "round")
  svg.appendChild(tip)
  
  // 2. Upper grip section
  const upperGrip = document.createElementNS("http://www.w3.org/2000/svg", "rect")
  upperGrip.setAttribute("x", "-7")
  upperGrip.setAttribute("y", "40")
  upperGrip.setAttribute("width", "14")
  upperGrip.setAttribute("height", "12")
  upperGrip.setAttribute("rx", "2")
  upperGrip.setAttribute("fill", "url(#bodyGrad)")
  upperGrip.setAttribute("stroke", "#000000")
  upperGrip.setAttribute("stroke-width", "1")
  svg.appendChild(upperGrip)
  
  // 3. Middle wider section
  const middle = document.createElementNS("http://www.w3.org/2000/svg", "rect")
  middle.setAttribute("x", "-14")
  middle.setAttribute("y", "52")
  middle.setAttribute("width", "28")
  middle.setAttribute("height", "7")
  middle.setAttribute("rx", "3.5")
  middle.setAttribute("fill", "url(#bodyGrad)")
  middle.setAttribute("stroke", "#000000")
  middle.setAttribute("stroke-width", "1")
  svg.appendChild(middle)
  
  // 4. Hatched body section
  const hatchedBody = document.createElementNS("http://www.w3.org/2000/svg", "rect")
  hatchedBody.setAttribute("x", "-8")
  hatchedBody.setAttribute("y", "59")
  hatchedBody.setAttribute("width", "16")
  hatchedBody.setAttribute("height", "25")
  hatchedBody.setAttribute("fill", "url(#bodyGrad)")
  hatchedBody.setAttribute("stroke", "#000000")
  hatchedBody.setAttribute("stroke-width", "1")
  svg.appendChild(hatchedBody)
  
  // Hatch lines
  const hatchGroup = document.createElementNS("http://www.w3.org/2000/svg", "g")
  hatchGroup.setAttribute("stroke", "#000000")
  hatchGroup.setAttribute("stroke-width", "0.5")
  hatchGroup.setAttribute("opacity", "0.4")
  for (const y of [65, 71, 77]) {
    const line = document.createElementNS("http://www.w3.org/2000/svg", "line")
    line.setAttribute("x1", "-8")
    line.setAttribute("y1", String(y))
    line.setAttribute("x2", "8")
    line.setAttribute("y2", String(y))
    hatchGroup.appendChild(line)
  }
  svg.appendChild(hatchGroup)
  
  // 5. Solid body section
  const solidBody = document.createElementNS("http://www.w3.org/2000/svg", "rect")
  solidBody.setAttribute("x", "-8")
  solidBody.setAttribute("y", "84")
  solidBody.setAttribute("width", "16")
  solidBody.setAttribute("height", "60")
  solidBody.setAttribute("rx", "1")
  solidBody.setAttribute("fill", "url(#bodyGrad)")
  solidBody.setAttribute("stroke", "#000000")
  solidBody.setAttribute("stroke-width", "1")
  svg.appendChild(solidBody)
  
  // 6. Bottom cap
  const cap = document.createElementNS("http://www.w3.org/2000/svg", "path")
  cap.setAttribute("d", "M-7,144 L7,144 L7,150 Q7,153 4,153 L-4,153 Q-7,153 -7,150 Z")
  cap.setAttribute("fill", "#c0c0c0")
  cap.setAttribute("stroke", "#777777")
  cap.setAttribute("stroke-width", "1")
  svg.appendChild(cap)
  
  return svg
}

// Line style dash patterns (in PDF points)
const PDF_LINE_DASHES: Record<LineStyle, number[]> = {
  solid: [],
  dotted: [1, 2],
  dashed: [4, 2],
  dashdot: [4, 2, 1, 2],
}

// Convert hex color to RGB array
function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (result) {
    return [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16),
    ]
  }
  return [0, 0, 0]
}

interface ExportOptions {
  layers: Layer[]
  canvasWidth: number
  canvasHeight: number
  pcbImage?: string | null
  imageWidth?: number
  imageHeight?: number
  imageGrayscale?: number
  imageOverlay?: string
  imageOverlayOpacity?: number
  fileName?: string
}

export async function exportVectorPDF(options: ExportOptions): Promise<void> {
  const {
    layers,
    pcbImage,
    fileName = "pcb-export.pdf",
  } = options

  // Get actual image dimensions if image exists
  let contentWidth = options.canvasWidth
  let contentHeight = options.canvasHeight
  
  if (pcbImage) {
    // Load image to get actual dimensions
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image()
      image.crossOrigin = "anonymous"
      image.onload = () => resolve(image)
      image.onerror = reject
      image.src = pcbImage
    })
    contentWidth = img.width
    contentHeight = img.height
  }

  // Determine orientation based on content aspect ratio
  const isLandscape = contentWidth > contentHeight
  const orientation = isLandscape ? "landscape" : "portrait"
  
  // A4 dimensions in mm
  const A4_W_MM = 297
  const A4_H_MM = 210
  const pageW = isLandscape ? A4_W_MM : A4_H_MM
  const pageH = isLandscape ? A4_H_MM : A4_W_MM
  
  // Create PDF document
  const pdf = new jsPDF({
    orientation,
    unit: "mm",
    format: "a4",
  })
  
  // Calculate scale to fit content into PDF page with margins (preserve aspect ratio)
  const margin = 10 // mm
  const availW = pageW - margin * 2
  const availH = pageH - margin * 2
  const scaleX = availW / contentWidth
  const scaleY = availH / contentHeight
  const scale = Math.min(scaleX, scaleY) // Use same scale for both axes to preserve aspect ratio
  
  // Calculate actual draw dimensions (proportional)
  const drawW = contentWidth * scale
  const drawH = contentHeight * scale
  
  // Center content on page
  const offsetX = margin + (availW - drawW) / 2
  const offsetY = margin + (availH - drawH) / 2
  
  // Transform canvas coordinates to PDF coordinates (uses same scale for X and Y)
  const toX = (x: number) => offsetX + x * scale
  const toY = (y: number) => offsetY + y * scale
  const toSize = (s: number) => s * scale
  
  // Add background image if present (as raster - unavoidable for bitmap)
  if (pcbImage) {
    try {
      // Use exact calculated dimensions to prevent stretching
      pdf.addImage(pcbImage, "PNG", offsetX, offsetY, drawW, drawH)
    } catch (e) {
      console.warn("Failed to add background image to PDF:", e)
    }
  }
  
  // Helper to set opacity using PDF graphics state
  const setOpacity = (strokeOpacity: number, fillOpacity: number) => {
    // jsPDF setGState method for transparency
    const gState = {
      'stroke-opacity': strokeOpacity,
      'fill-opacity': fillOpacity,
    }
    // Use internal API to set graphics state
    pdf.setGState(pdf.GState(gState))
  }
  
  // Collect SVG probes for async rendering
  interface SvgProbeInfo {
    svg: SVGSVGElement
    x: number
    y: number
    width: number
    height: number
    rotation: number
    centerX: number
    centerY: number
  }
  const svgProbes: SvgProbeInfo[] = []
  
  // Draw all objects from all visible layers
  for (const layer of layers) {
    if (!layer.visible) continue
    
    const layerOpacity = (layer.opacity ?? 100) / 100
    
    for (const obj of layer.objects) {
      if (obj.visible === false) continue
      
      const [r, g, b] = hexToRgb(obj.color)
      
      // Set line style
      const lineStyle = obj.lineStyle || "solid"
      const dashes = PDF_LINE_DASHES[lineStyle]
      if (dashes.length > 0) {
        pdf.setLineDashPattern(dashes.map(d => d * scale), 0)
      } else {
        pdf.setLineDashPattern([], 0)
      }
      
      // Set stroke properties with layer opacity
      pdf.setDrawColor(r, g, b)
      pdf.setLineWidth(toSize(obj.strokeWidth))
      pdf.setLineCap("round")
      pdf.setLineJoin("round")
      
      // Apply layer opacity to strokes
      setOpacity(layerOpacity, layerOpacity)
      
      switch (obj.type) {
        case "line":
        case "measure": {
          if (obj.points.length >= 2) {
            const p0 = obj.points[0]
            const p1 = obj.points[1]
            pdf.line(toX(p0.x), toY(p0.y), toX(p1.x), toY(p1.y))
            
            // Draw measurement text if measure type
            if (obj.type === "measure") {
              const dx = p1.x - p0.x
              const dy = p1.y - p0.y
              const dist = Math.sqrt(dx * dx + dy * dy)
              const midX = (p0.x + p1.x) / 2
              const midY = (p0.y + p1.y) / 2
              
              pdf.setFontSize(toSize(10) * 2.83) // Convert mm to points
              pdf.setTextColor(r, g, b)
              pdf.text(`${dist.toFixed(1)}px`, toX(midX), toY(midY) - toSize(5))
            }
          }
          break
        }
        
        case "polyline":
        case "freehand": {
          if (obj.points.length >= 2) {
            // Draw as connected lines using pdf.lines()
            const startX = toX(obj.points[0].x)
            const startY = toY(obj.points[0].y)
            
            // Create line segments relative to the start point
            const lineSegments: [number, number][] = []
            for (let i = 1; i < obj.points.length; i++) {
              const prevX = toX(obj.points[i - 1].x)
              const prevY = toY(obj.points[i - 1].y)
              const currX = toX(obj.points[i].x)
              const currY = toY(obj.points[i].y)
              lineSegments.push([currX - prevX, currY - prevY])
            }
            
            if (lineSegments.length > 0) {
              pdf.lines(lineSegments, startX, startY, [1, 1], "S", false)
            }
          }
          break
        }
        
        case "polygon": {
          if (obj.points.length >= 3) {
            const startX = toX(obj.points[0].x)
            const startY = toY(obj.points[0].y)
            
            // Create line segments relative to the previous point
            const lineSegments: [number, number][] = []
            for (let i = 1; i < obj.points.length; i++) {
              const prevX = toX(obj.points[i - 1].x)
              const prevY = toY(obj.points[i - 1].y)
              const currX = toX(obj.points[i].x)
              const currY = toY(obj.points[i].y)
              lineSegments.push([currX - prevX, currY - prevY])
            }
            // Close the polygon
            const lastX = toX(obj.points[obj.points.length - 1].x)
            const lastY = toY(obj.points[obj.points.length - 1].y)
            lineSegments.push([startX - lastX, startY - lastY])
            
            if (obj.filled) {
              // Apply fill opacity (fillOpacity is 0-100, convert to 0-1, multiply by layer opacity)
              const fillOp = ((obj.fillOpacity ?? 50) / 100) * layerOpacity
              setOpacity(layerOpacity, fillOp)
              pdf.setFillColor(r, g, b)
              
              // Draw filled polygon
              pdf.lines(lineSegments, startX, startY, [1, 1], "F", true)
              
              // Reset opacity for stroke
              setOpacity(layerOpacity, layerOpacity)
            }
            
            // Draw polygon outline
            pdf.lines(lineSegments, startX, startY, [1, 1], "S", true)
          }
          break
        }
        
        case "rect": {
          if (obj.points.length >= 2) {
            const [p0, p1] = obj.points
            const x = toX(Math.min(p0.x, p1.x))
            const y = toY(Math.min(p0.y, p1.y))
            const w = toSize(Math.abs(p1.x - p0.x))
            const h = toSize(Math.abs(p1.y - p0.y))
            
            if (obj.filled) {
              const fillOp = ((obj.fillOpacity ?? 50) / 100) * layerOpacity
              setOpacity(layerOpacity, fillOp)
              pdf.setFillColor(r, g, b)
              pdf.rect(x, y, w, h, "F")
              setOpacity(layerOpacity, layerOpacity)
            }
            
            pdf.rect(x, y, w, h, "S")
          }
          break
        }
        
        case "circle": {
          if (obj.points.length >= 2) {
            const center = obj.points[0]
            const edge = obj.points[1]
            const radius = Math.sqrt(
              (edge.x - center.x) ** 2 + (edge.y - center.y) ** 2
            )
            
            const cx = toX(center.x)
            const cy = toY(center.y)
            const r_pdf = toSize(radius)
            
            if (obj.filled) {
              const fillOp = ((obj.fillOpacity ?? 50) / 100) * layerOpacity
              setOpacity(layerOpacity, fillOp)
              pdf.setFillColor(r, g, b)
              pdf.circle(cx, cy, r_pdf, "F")
              setOpacity(layerOpacity, layerOpacity)
            }
            
            pdf.circle(cx, cy, r_pdf, "S")
          }
          break
        }
        
        case "text": {
          if (obj.points.length >= 1 && obj.text) {
            const pos = obj.points[0]
            const fontSize = obj.fontSize ?? 16
            const fontFamily = obj.fontFamily ?? "helvetica"
            
            // Map font family to jsPDF font
            let pdfFont = "helvetica"
            if (fontFamily.toLowerCase().includes("mono") || fontFamily.toLowerCase().includes("courier")) {
              pdfFont = "courier"
            } else if (fontFamily.toLowerCase().includes("times") || fontFamily.toLowerCase().includes("serif")) {
              pdfFont = "times"
            }
            
            // Set font style
            let fontStyle = "normal"
            if (obj.fontBold && obj.fontItalic) fontStyle = "bolditalic"
            else if (obj.fontBold) fontStyle = "bold"
            else if (obj.fontItalic) fontStyle = "italic"
            
            pdf.setFont(pdfFont, fontStyle)
            pdf.setFontSize(toSize(fontSize) * 2.83) // mm to points
            pdf.setTextColor(r, g, b)
            
            // Handle rotation
            if (obj.rotation) {
              pdf.saveGraphicsState()
              const x = toX(pos.x)
              const y = toY(pos.y)
              // jsPDF text rotation
              pdf.text(obj.text, x, y, { angle: -obj.rotation })
              pdf.restoreGraphicsState()
            } else {
              pdf.text(obj.text, toX(pos.x), toY(pos.y))
            }
            
            // Underline
            if (obj.fontUnderline) {
              const textWidth = pdf.getTextWidth(obj.text)
              const underlineY = toY(pos.y) + toSize(2)
              pdf.setLineWidth(toSize(0.5))
              pdf.line(toX(pos.x), underlineY, toX(pos.x) + textWidth, underlineY)
            }
          }
          break
        }
        
        case "marker": {
          if (obj.points.length >= 1) {
            const pos = obj.points[0]
            const markerSize = obj.markerSize ?? 12
            const symbol = obj.markerSymbol ?? "circle"
            const filled = obj.markerFilled ?? true
            const hs = toSize(markerSize / 2)
            
            const cx = toX(pos.x)
            const cy = toY(pos.y)
            
            pdf.setFillColor(r, g, b)
            
            switch (symbol) {
              case "circle":
                pdf.circle(cx, cy, hs, filled ? "F" : "S")
                break
              case "rectangle":
                pdf.rect(cx - hs, cy - hs, hs * 2, hs * 2, filled ? "F" : "S")
                break
              case "diamond": {
                // Draw diamond shape
                pdf.moveTo(cx, cy - hs)
                pdf.lineTo(cx + hs, cy)
                pdf.lineTo(cx, cy + hs)
                pdf.lineTo(cx - hs, cy)
                pdf.lineTo(cx, cy - hs)
                if (filled) pdf.fill()
                else pdf.stroke()
                break
              }
            }
          }
          break
        }
        
        case "probe": {
          if (obj.points.length >= 2) {
            const head = obj.points[0] // tip position
            const tail = obj.points[1] // tail position
            const probeSize = obj.probeSize ?? 90
            
            // Calculate angle from tail to head (in degrees for SVG rotation)
            const angleRad = Math.atan2(head.y - tail.y, head.x - tail.x) + Math.PI / 2
            const angleDeg = (angleRad * 180) / Math.PI
            
            // Create the probe SVG
            const probeSvg = createProbeSVG(obj.color, probeSize)
            
            // Calculate PDF position - SVG will be placed with its top-left at (x, y)
            // We need to position so the tip (top center of SVG) is at head position
            const svgWidth = toSize(30 * (probeSize / 153))
            const svgHeight = toSize(probeSize)
            
            // Store probe for async rendering
            svgProbes.push({
              svg: probeSvg,
              x: toX(head.x) - svgWidth / 2,
              y: toY(head.y),
              width: svgWidth,
              height: svgHeight,
              rotation: angleDeg,
              centerX: toX(head.x),
              centerY: toY(head.y),
            })
          }
          break
        }
      }
      
      // Draw comment if present
      if (obj.comment && obj.comment.trim()) {
        const anchor = obj.points[0] || { x: 0, y: 0 }
        const offset = obj.commentOffset ?? { x: 60, y: -40 }
        const boxX = anchor.x + offset.x
        const boxY = anchor.y + offset.y
        
        // Comment box
        const padding = 6
        const fontSize = 10
        pdf.setFontSize(toSize(fontSize) * 2.83)
        pdf.setFont("helvetica", "normal")
        const textWidth = pdf.getTextWidth(obj.comment) / scale
        const boxW = textWidth + padding * 2
        const boxH = fontSize + padding * 2
        
        // Draw comment background
        pdf.setFillColor(255, 255, 255)
        pdf.setDrawColor(r, g, b)
        pdf.setLineWidth(toSize(1))
        pdf.roundedRect(toX(boxX - boxW / 2), toY(boxY - boxH / 2), toSize(boxW), toSize(boxH), toSize(3), toSize(3), "FD")
        
        // Draw comment text
        pdf.setTextColor(r, g, b)
        pdf.text(obj.comment, toX(boxX), toY(boxY + fontSize / 3), { align: "center" })
        
        // Draw wire from anchor to comment
        pdf.setLineDashPattern([1, 1], 0)
        pdf.line(toX(anchor.x), toY(anchor.y), toX(boxX), toY(boxY))
        pdf.setLineDashPattern([], 0)
      }
    }
  }
  
  // Render SVG probes using svg2pdf.js
  for (const probe of svgProbes) {
    // Create a wrapper SVG with rotation transform
    const wrapperSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    wrapperSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg")
    wrapperSvg.setAttribute("width", String(probe.width * 3))
    wrapperSvg.setAttribute("height", String(probe.height * 3))
    
    // Create a group with rotation transform
    const g = document.createElementNS("http://www.w3.org/2000/svg", "g")
    g.setAttribute("transform", `translate(${probe.width * 1.5}, ${probe.height * 0.5}) rotate(${probe.rotation})`)
    
    // Clone the probe SVG content into the group
    const probeClone = probe.svg.cloneNode(true) as SVGSVGElement
    probeClone.setAttribute("x", String(-probe.width / 2))
    probeClone.setAttribute("y", "0")
    g.appendChild(probeClone)
    wrapperSvg.appendChild(g)
    
    // Add to document temporarily for svg2pdf
    document.body.appendChild(wrapperSvg)
    
    try {
      await pdf.svg(wrapperSvg, {
        x: probe.centerX - probe.width * 1.5,
        y: probe.centerY - probe.height * 0.5,
        width: probe.width * 3,
        height: probe.height * 3,
      })
    } catch (e) {
      console.warn("Failed to render probe SVG:", e)
    } finally {
      document.body.removeChild(wrapperSvg)
    }
  }
  
  // Save the PDF
  pdf.save(fileName)
}

export type { ExportOptions }
